import { useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

interface IdleWarningModalProps {
  isOpen: boolean;
  secondsRemaining: number;
  onStaySignedIn: () => void;
  onSignOutNow: () => void;
}

function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;

  return minutes > 0
    ? `${minutes}:${String(seconds).padStart(2, '0')}`
    : `${seconds}s`;
}

/**
 * "You're about to be signed out" countdown — the replacement for legacy's
 * jQuery UI idle dialog.
 *
 * Two deliberate details:
 *
 * z-[10002] — Modal's own wrapper is z-50, which would put this UNDER the
 * floating video/voice call cards and the note form (z-[9999]) and under toasts
 * (z-[10001]). A warning the user can't see is worse than none.
 *
 * No backdrop or Escape dismissal — the only ways out are the two buttons.
 * Dismissing by clicking away would cancel the client's countdown without
 * telling the server the user is still there, so the session would expire anyway
 * and the logout would arrive with no warning at all.
 */
export function IdleWarningModal({
  isOpen,
  secondsRemaining,
  onStaySignedIn,
  onSignOutNow,
}: IdleWarningModalProps) {
  const previousTitle = useRef<string | null>(null);

  // Mirror the countdown into the tab title, so a user looking at another tab
  // still gets a chance to notice.
  useEffect(() => {
    if (!isOpen) return;
    if (previousTitle.current === null) {
      previousTitle.current = document.title;
    }
    document.title = `(${formatCountdown(secondsRemaining)}) Signing out — ${previousTitle.current}`;

    return () => {
      if (previousTitle.current !== null) {
        document.title = previousTitle.current;
        previousTitle.current = null;
      }
    };
  }, [isOpen, secondsRemaining]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onStaySignedIn}
      size="sm"
      title="Still there?"
      closeOnBackdrop={false}
      closeOnEscape={false}
      hideCloseButton
      wrapperClassName="z-[10002]"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onSignOutNow}>
            Sign out now
          </Button>
          <Button variant="primary" onClick={onStaySignedIn} autoFocus>
            Stay signed in
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <Clock className="h-5 w-5" aria-hidden />
        </span>
        <div className="space-y-1">
          <p className="text-sm text-slate-700">
            You&rsquo;ll be signed out due to inactivity in{' '}
            <span
              className="font-semibold tabular-nums text-slate-900"
              // Announce each tick to screen readers without stealing focus.
              role="timer"
              aria-live="polite"
            >
              {formatCountdown(secondsRemaining)}
            </span>
            .
          </p>
          <p className="text-xs text-slate-500">
            Any unsaved work will be lost. Choose &ldquo;Stay signed in&rdquo; to continue.
          </p>
        </div>
      </div>
    </Modal>
  );
}
